import { type JSX } from "react";
import { useWorldlineTree } from "../../api/hooks/useWorldlineTree";
import { buildWorldlineTree, type WorldlineTreeNode } from "./buildWorldlineTree";
import { WorldlineTreeNodeView } from "./WorldlineTreeNodeView";
import { mapProblemDetail } from "../../errors/mapProblemDetail";
import { ErrorBanner } from "../player/ErrorBanner";

export type WorldlineTreeViewProps = {
  scenarioId: string;
  /** 現在プレイ中の世界線 ID。該当ノードをハイライト表示する */
  currentWorldlineId: string;
  /**
   * 「ここから分岐」ボタンが押されたときに呼ばれるコールバック。
   * fork に必要な branchFromSceneId/branchFromChoiceId はツリービューでは未確定のため、
   * 呼び出し元（PlayerScreen など）が BackscrollView を開いて選択させる設計とする。
   * 省略した場合はボタンを押しても何も起きない。
   */
  onForkRequest?: ((worldlineId: string) => void) | undefined;
  /** fork 成功時に新しい世界線 ID を渡すコールバック（将来の直接 fork に備えた拡張点） */
  onForkSuccess?: ((newWorldlineId: string) => void) | undefined;
};

/**
 * 世界線ツリーを CSS インデント付きリストで表示するコンポーネント。
 *
 * - useWorldlineTree でフラット配列を取得し、buildWorldlineTree でネストツリーに変換する
 * - 現在の世界線ノードを font-bold + ring でハイライトする
 * - エラー時はエラーバナー + 再試行ボタンを表示する
 * - 「ここから分岐」ボタンは onForkRequest コールバックを呼ぶ。
 *   実際の fork API 呼び出しは呼び出し元が担当する（design.md 5.3 参照）。
 */
export function WorldlineTreeView({
  scenarioId,
  currentWorldlineId,
  onForkRequest,
}: WorldlineTreeViewProps): JSX.Element {
  const { data: nodes, isError, error, refetch } = useWorldlineTree(scenarioId);

  if (isError) {
    const uiError = mapProblemDetail(error);
    return (
      <div data-testid="worldline-tree-view-error" className="p-4">
        <ErrorBanner
          error={uiError}
          {...(uiError.retryable ? { onRetry: () => { void refetch(); } } : {})}
        />
      </div>
    );
  }

  if (nodes === undefined) {
    return (
      <div data-testid="worldline-tree-view-loading" className="p-4 text-slate-400">
        世界線ツリーを読み込み中…
      </div>
    );
  }

  const treeRoots = buildWorldlineTree(nodes);

  return (
    <div data-testid="worldline-tree-view" className="flex flex-col gap-0.5 p-2">
      {treeRoots.map((root) => (
        <WorldlineTreeNodeSubtree
          key={root.id}
          node={root}
          depth={0}
          currentWorldlineId={currentWorldlineId}
          {...(onForkRequest !== undefined ? { onForkRequest } : {})}
        />
      ))}
    </div>
  );
}

type WorldlineTreeNodeSubtreeProps = {
  node: WorldlineTreeNode;
  depth: number;
  currentWorldlineId: string;
  onForkRequest?: ((worldlineId: string) => void) | undefined;
};

/**
 * ノードとその子孫を再帰的にレンダリングする内部コンポーネント。
 * WorldlineTreeNodeView を depth 付きで呼び出し、子ノードを再帰的に描画する。
 */
function WorldlineTreeNodeSubtree({
  node,
  depth,
  currentWorldlineId,
  onForkRequest,
}: WorldlineTreeNodeSubtreeProps): JSX.Element {
  return (
    <>
      <WorldlineTreeNodeView
        node={node}
        isHighlighted={node.id === currentWorldlineId}
        scenarioId=""
        depth={depth}
        {...(onForkRequest !== undefined ? { onForkRequest } : {})}
      />
      {node.children.map((child) => (
        <WorldlineTreeNodeSubtree
          key={child.id}
          node={child}
          depth={depth + 1}
          currentWorldlineId={currentWorldlineId}
          {...(onForkRequest !== undefined ? { onForkRequest } : {})}
        />
      ))}
    </>
  );
}
